import React, { useState, useEffect } from 'react'
import { X, Mail, MessageSquare, User } from 'lucide-react'
import { Button } from '@rallyshift/design-system'
import api from '../api/client'
import { useLocale } from '../contexts/LocaleContext'
import { useOpenConversation } from '../hooks/useOpenConversation'

interface ContactAdminModalProps {
  isOpen: boolean
  onClose: () => void
}

interface AdminProfile {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  phone_number: string | null
}

const ContactAdminModal: React.FC<ContactAdminModalProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useLocale()
  const [admins, setAdmins] = useState<AdminProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const { open: openChat, opening, error: chatError } = useOpenConversation()

  useEffect(() => {
    if (isOpen) {
      fetchAdmins()
    }
  }, [isOpen])

  const fetchAdmins = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await api.profiles.listAdmins()
      setAdmins(data || [])
    } catch (err: any) {
      console.error('Error fetching admins:', err)
      setError(err.message || 'Failed to fetch admin contact information')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={onClose}
        ></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t('contactAdmin.title')}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                {t('contactAdmin.message')}
              </p>
            </div>

            {(error || chatError) && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error || chatError}</div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">{t('common.loading')}</div>
              </div>
            ) : admins.length > 0 ? (
              <div className="space-y-3">
                {admins.map((admin) => (
                  <div
                    key={admin.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {admin.first_name || admin.last_name
                            ? `${admin.first_name || ''} ${admin.last_name || ''}`.trim()
                            : t('contactAdmin.admin')}
                        </p>
                        <div className="mt-2 space-y-1">
                          <a
                            href={`mailto:${admin.email}`}
                            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            {admin.email}
                          </a>
                          {admin.phone_number && (
                            <p className="text-sm text-gray-600">
                              {admin.phone_number}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <Button
                          variant="secondary"
                          icon={<MessageSquare className="h-4 w-4" />}
                          loading={opening}
                          onClick={() => openChat(() => api.messaging.openDirect(admin.id))}
                        >
                          {t('messages.message')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">
                  {t('contactAdmin.noAdmins')}
                </p>
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContactAdminModal

